import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

/**
 * Interactive 3D iPhone. Tilts toward the pointer, layered depth,
 * moving glass reflection. Pure CSS 3D + framer-motion springs.
 */
export default function Interactive3DPhone() {
  const ref = useRef(null);

  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);

  const rx = useSpring(useTransform(my, [0, 1], [18, -18]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [0, 1], [-24, 24]), { stiffness: 120, damping: 14 });

  const glareX = useTransform(mx, [0, 1], ["12%", "88%"]);
  const glareY = useTransform(my, [0, 1], ["8%", "92%"]);

  const handleMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width);
    my.set((e.clientY - rect.top) / rect.height);
  };
  const handleLeave = () => {
    mx.set(0.5);
    my.set(0.5);
  };

  return (
    <div
      ref={ref}
      data-testid="interactive-3d-phone"
      onPointerMove={handleMove}
      onPointerLeave={handleLeave}
      className="relative w-full h-full flex items-center justify-center select-none"
      style={{ perspective: 1200 }}
    >
      {/* ambient glow */}
      <div className="absolute w-[360px] h-[360px] rounded-full bg-white/10 blur-[100px]" />

      <motion.div
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d", willChange: "transform" }}
        initial={{ opacity: 0, y: 60, rotateY: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
        className="relative"
      >
        {/* Phone body */}
        <div
          className="relative w-[248px] h-[520px] sm:w-[280px] sm:h-[588px] rounded-[3rem] p-[3px]"
          style={{
            transformStyle: "preserve-3d",
            background: "linear-gradient(145deg,#3a3a3d,#0c0c0e 55%,#2a2a2d)",
            boxShadow:
              "0 40px 120px rgba(0,0,0,0.7), inset 0 0 2px rgba(255,255,255,0.4)",
          }}
        >
          {/* titanium rim */}
          <div className="absolute inset-0 rounded-[3rem] ring-1 ring-white/10" />

          {/* Screen */}
          <div
            className="relative w-full h-full rounded-[2.8rem] overflow-hidden bg-black"
            style={{ transform: "translateZ(6px)" }}
          >
            <img
              src="https://images.pexels.com/photos/37467615/pexels-photo-37467615.jpeg"
              alt="iPhone Display"
              className="absolute inset-0 w-full h-full object-cover opacity-70"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/60" />

            {/* Dynamic island */}
            <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-7 rounded-full bg-black z-20 ring-1 ring-white/10" />

            {/* screen content */}
            <div className="absolute inset-0 z-10 flex flex-col justify-between p-7">
              <div className="mt-14 text-center">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                  G-Repair
                </p>
                <p className="font-display text-4xl text-white mt-3 leading-none">
                  Wie neu.
                </p>
              </div>
              <div className="space-y-2">
                <div className="bg-white/10 border border-white/15 rounded-2xl px-4 py-3 flex items-center justify-between">
                  <span className="text-white text-sm">Display</span>
                  <span className="font-mono text-white/70 text-xs">ab 59 €</span>
                </div>
                <div className="bg-white/10 border border-white/15 rounded-2xl px-4 py-3 flex items-center justify-between">
                  <span className="text-white text-sm">Akkutausch</span>
                  <span className="font-mono text-white/70 text-xs">ab 39 €</span>
                </div>
              </div>
            </div>

            {/* moving glare */}
            <motion.div
              className="absolute w-56 h-56 rounded-full bg-white/20 blur-3xl pointer-events-none z-30"
              style={{ left: glareX, top: glareY, x: "-50%", y: "-50%" }}
            />
          </div>

          {/* side buttons */}
          <div className="absolute -left-[3px] top-28 w-[3px] h-8 rounded-l bg-[#4a4a4d]" style={{ transform: "translateZ(-2px)" }} />
          <div className="absolute -left-[3px] top-40 w-[3px] h-12 rounded-l bg-[#4a4a4d]" style={{ transform: "translateZ(-2px)" }} />
          <div className="absolute -right-[3px] top-36 w-[3px] h-16 rounded-r bg-[#4a4a4d]" style={{ transform: "translateZ(-2px)" }} />
        </div>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute -bottom-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#666]"
      >
        ↖ Bewege die Maus
      </motion.p>
    </div>
  );
}
